(() => {
  const proxyBase = '/apps/inknfiber-wishlist';
  const buttonSelector = '[data-inknfiber-wishlist-button]';
  let statePromise = null;
  let savedProducts = [];
  let isGuest = false;

  const normalizeProductId = (value) => String(value || '').match(/(\d+)$/)?.[1] || '';
  const productKey = (item) => normalizeProductId(item.product_id || item.productId || '') || String(item.handle || '');

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatPrice(item) {
    const amount = Number(item.price || 0);
    if (!Number.isFinite(amount) || amount <= 0) return '';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: item.currency_code || 'USD',
      }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }

  function showToast(message, linkUrl, linkText = 'Log in') {
    const existing = document.querySelector('.inknfiber-wishlist-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'inknfiber-wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML = `${escapeHtml(message)}${linkUrl ? ` <a href="${escapeHtml(linkUrl)}">${escapeHtml(linkText)}</a>` : ''}`;
    document.body.append(toast);
    window.setTimeout(() => toast.remove(), 5200);
  }

  async function wishlistRequest(path, options = {}) {
    const separator = path && path.startsWith('?') ? '' : '?';
    const response = await fetch(`${proxyBase}${separator}${path}`.replace(/\?$/, ''), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...options,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      const error = new Error(data.error || 'Wishlist is unavailable right now.');
      error.status = response.status;
      throw error;
    }
    return data;
  }

  async function loadState(includeProducts = false) {
    if (!statePromise || includeProducts) {
      statePromise = wishlistRequest(includeProducts ? 'action=get&include_products=1' : 'action=get').catch((error) => {
        if (error.status === 401) {
          isGuest = true;
          savedProducts = [];
          syncButtons();
          return { products: [], items: [], guest: true };
        }
        throw error;
      });
    }

    const data = await statePromise;
    savedProducts = Array.isArray(data.products) ? data.products : [];
    isGuest = data.guest === true;
    syncButtons();
    return data;
  }

  function isSaved(button) {
    const key = productKey({
      product_id: button.dataset.productId,
      handle: button.dataset.productHandle,
    });
    return savedProducts.some((item) => productKey(item) === key || item.handle === button.dataset.productHandle);
  }

  function syncButtons() {
    document.querySelectorAll(buttonSelector).forEach((button) => {
      const saved = isSaved(button);
      const label = button.querySelector('[data-inknfiber-wishlist-label]');
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Save to wishlist');
      if (label) {
        const isProductButton = button.classList.contains('inknfiber-wishlist-button--product');
        label.textContent = saved ? 'Saved' : (isProductButton ? 'Save to wishlist' : 'Save');
      }
    });
  }

  async function toggleButton(button) {
    await loadState().catch(() => null);
    if (isGuest) {
      showToast('Please log in to save products.', button.dataset.loginUrl || '/account/login');
      return;
    }

    const saved = isSaved(button);
    const path = saved ? 'action=remove' : 'action=add';
    button.disabled = true;
    try {
      const data = await wishlistRequest(path, {
        method: 'POST',
        body: JSON.stringify({
          product_id: button.dataset.productId || '',
          handle: button.dataset.productHandle || '',
        }),
      });
      savedProducts = Array.isArray(data.products) ? data.products : [];
      statePromise = Promise.resolve(data);
      syncButtons();
      document.dispatchEvent(new CustomEvent('inknfiber:wishlist:updated', { detail: data }));
      showToast(saved ? 'Removed from wishlist.' : 'Saved to wishlist.', saved ? '' : '/pages/wishlist', 'View wishlist');
    } catch (error) {
      if (error.status === 401) {
        isGuest = true;
        showToast('Please log in to save products.', button.dataset.loginUrl || '/account/login');
      } else {
        showToast(error.message || 'Wishlist is unavailable right now.');
      }
    } finally {
      button.disabled = false;
    }
  }

  function renderWishlistPage(data) {
    const page = document.querySelector('[data-inknfiber-wishlist-page]');
    if (!page) return;

    const status = page.querySelector('[data-wishlist-status]');
    const grid = page.querySelector('[data-wishlist-grid]');
    if (!status || !grid) return;

    const items = Array.isArray(data.items) ? data.items : [];
    if (isGuest) {
      status.innerHTML = `Please log in to view saved products. <a href="${escapeHtml(page.dataset.loginUrl || '/account/login')}">Log in</a>`;
      grid.innerHTML = '';
      return;
    }
    if (!items.length) {
      status.textContent = 'Your wishlist is empty.';
      grid.innerHTML = '';
      return;
    }

    status.textContent = `${items.length} saved product${items.length === 1 ? '' : 's'}`;
    grid.innerHTML = items.map((item) => `
      <article class="inknfiber-wishlist-card">
        <a href="${escapeHtml(item.url || `/products/${item.handle}`)}" class="inknfiber-wishlist-card__media">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.image_alt || item.title)}" loading="lazy">` : '<span aria-hidden="true"></span>'}
        </a>
        <div class="inknfiber-wishlist-card__body">
          <a href="${escapeHtml(item.url || `/products/${item.handle}`)}">${escapeHtml(item.title || 'Saved product')}</a>
          ${formatPrice(item) ? `<p>${escapeHtml(formatPrice(item))}</p>` : ''}
          <button type="button" class="inknfiber-wishlist-remove" data-inknfiber-wishlist-remove data-product-id="${escapeHtml(item.product_id)}" data-product-handle="${escapeHtml(item.handle)}">Remove</button>
        </div>
      </article>
    `).join('');
  }

  async function initWishlistPage() {
    const page = document.querySelector('[data-inknfiber-wishlist-page]');
    if (!page) return;
    const status = page.querySelector('[data-wishlist-status]');
    if (status) status.textContent = 'Loading saved products...';
    try {
      renderWishlistPage(await loadState(true));
    } catch (error) {
      if (status) status.textContent = error.message || 'Wishlist is unavailable right now.';
    }
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest(buttonSelector);
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      await toggleButton(button);
      return;
    }

    const removeButton = event.target.closest('[data-inknfiber-wishlist-remove]');
    if (removeButton) {
      removeButton.disabled = true;
      try {
        const data = await wishlistRequest('action=remove', {
          method: 'POST',
          body: JSON.stringify({
            product_id: removeButton.dataset.productId || '',
            handle: removeButton.dataset.productHandle || '',
          }),
        });
        savedProducts = Array.isArray(data.products) ? data.products : [];
        statePromise = Promise.resolve(data);
        syncButtons();
        renderWishlistPage(data);
      } catch (error) {
        showToast(error.message || 'Wishlist is unavailable right now.');
        removeButton.disabled = false;
      }
    }
  });

  document.addEventListener('inknfiber:wishlist:updated', (event) => renderWishlistPage(event.detail || {}));
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector(buttonSelector)) loadState().catch(() => null);
    initWishlistPage();
  });
})();
